import {
  Draft,
  DraftComplete,
  DraftPartial,
  GuessModes,
  Settings,
  SettingsCompress,
  TeamBrawler,
  TeamBrawlerComplete,
  TeamBrawlerCompress,
  TeamColors,
  TeamComplete,
} from '../common.type';
import { ensureTeamIdx } from './ensureTeamIdx';
import { ensureIsDraftComplete } from './ensureIsDraftComplete';

const mapMinus = 15000000;
const brawlerMinus = 16000000;
const starPowerMinus = 23000000;
const gadgetMinus = 23000000;

const TeamEmpty = { id: null, ban: null, starPower: null, gadget: null };

const compressBrawler = (b: TeamBrawlerComplete): TeamBrawlerCompress => {
  return {
    i: (b.id - brawlerMinus).toString(16),
    b: (b.ban - brawlerMinus).toString(16),
    s: (b.starPower - starPowerMinus).toString(16),
    g: (b.gadget - gadgetMinus).toString(16),
  };
};

const brawlerStringify = (b: TeamBrawlerCompress): string => {
  return Object.keys(b)
    .map((k) => `${k}${b[k as keyof TeamBrawlerCompress]}`)
    .join(':');
};

const settingsStringify = (s: SettingsCompress): string => {
  return Object.keys(s)
    .map((k) => `${k}${s[k as keyof SettingsCompress]}`)
    .join(':');
};
const draftStringify = (s: DraftCompress): string => {
  return Object.keys(s)
    .map((k) => `${k}${s[k as keyof DraftCompress]}`)
    .join('|');
};

const compressTeam = (t: TeamComplete): string => {
  return [compressBrawler(t[1]), compressBrawler(t[2]), compressBrawler(t[3])]
    .map((t) => brawlerStringify(t))
    .join(';');
};
const compressSettings = (s: Settings): SettingsCompress => {
  const c = s.coin === TeamColors.BLUE ? 'b' : 'r';
  const m =
    s.mode === GuessModes.TWO_LAST
      ? '2'
      : s.mode === GuessModes.CUSTOM
        ? 'c'
        : 'l';
  return {
    c,
    m,
    t: s.tries.toString(),
  };
};

type DraftCompress = { b: string; r: string; m: string; s: string };

export const compress = (d: Draft) => {
  if (!ensureIsDraftComplete(d)) return '';

  const draftCompress: DraftCompress = {
    b: compressTeam(d.blueTeam),
    r: compressTeam(d.redTeam),
    m: (d.mapId - mapMinus).toString(16),
    s: settingsStringify(compressSettings(d.settings)),
  };

  return draftStringify(draftCompress);
};

const getFirstChar = (str: string): { key: string; value: string } => {
  const key = str.slice(0, 1);
  const value = str.slice(1);
  return { key, value };
};

const decompressBrawler = (str: string): TeamBrawler => {
  const team: TeamBrawler = {
    1: { ...TeamEmpty },
    2: { ...TeamEmpty },
    3: { ...TeamEmpty },
  };
  const values = str.split(';');
  values.forEach((brawlerStr, i) => {
    const idx = i + 1;
    if (!ensureTeamIdx(idx)) return;
    brawlerStr.split(':').forEach((bPStr) => {
      const { key: kP, value: vP } = getFirstChar(bPStr);
      switch (kP) {
        case 'i':
          team[idx].id = parseInt(vP, 16) + brawlerMinus;
          break;
        case 'b':
          team[idx].ban = parseInt(vP, 16) + brawlerMinus;
          break;
        case 's':
          team[idx].starPower = parseInt(vP, 16) + starPowerMinus;
          break;
        case 'g':
          team[idx].gadget = parseInt(vP, 16) + gadgetMinus;
          break;
      }
    });
  });
  return team;
};

export const decompress = (str: string): DraftComplete | undefined => {
  const draft: DraftPartial = {
    blueTeam: {
      1: { ...TeamEmpty },
      2: { ...TeamEmpty },
      3: { ...TeamEmpty },
    },
    redTeam: {
      1: { ...TeamEmpty },
      2: { ...TeamEmpty },
      3: { ...TeamEmpty },
    },
    mapId: null,
    settings: { coin: null, mode: null, tries: null },
  };

  str.split('|').forEach((v) => {
    const { key, value } = getFirstChar(v);
    switch (key) {
      case 'b':
        draft.blueTeam = decompressBrawler(value);
        break;
      case 'r':
        draft.redTeam = decompressBrawler(value);
        break;
      case 'm':
        draft.mapId = parseInt(value, 16) + mapMinus;
        break;
      case 's':
        value.split(':').forEach((bPStr) => {
          const { key: kP, value: vP } = getFirstChar(bPStr);
          switch (kP) {
            case 'c':
              draft.settings.coin =
                vP === 'b' ? TeamColors.BLUE : TeamColors.RED;
              break;
            case 'm':
              draft.settings.mode =
                vP === 'c'
                  ? GuessModes.CUSTOM
                  : vP === '2'
                    ? GuessModes.TWO_LAST
                    : GuessModes.LAST;
              break;
            case 't':
              draft.settings.tries = parseInt(vP, 16);
              break;
          }
        });
        break;
    }
  });
  if (ensureIsDraftComplete(draft)) {
    return draft;
  }
  return;
};
